import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Footprints, Eye, EyeOff, RotateCcw, Share2, Globe, X } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';

import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What is a Digital Footprint?",
        icon: "👣",
        content: (
            <div className="space-y-6">
                <div className="bg-indigo-50 p-6 rounded-2xl border-2 border-indigo-200">
                    <h3 className="font-bold text-xl text-indigo-800 mb-3">Your Online Trail</h3>
                    <p className="text-slate-700 mb-4">
                        Every time you do something online - post a photo, like a video, search for something,
                        or even just visit a website - you leave a <strong>digital footprint</strong>.
                    </p>
                    <p className="text-slate-700">
                        Just like footprints in sand, these traces can be seen by others. Unlike sand,
                        digital footprints can last <strong>forever</strong>!
                    </p>
                </div>
                <div className="bg-purple-50 p-5 rounded-xl border border-purple-200">
                    <h4 className="font-bold text-purple-800 mb-3">👣 Your Footprint Includes:</h4>
                    <ul className="text-purple-700 space-y-2">
                        <li className="flex items-center gap-2"><Footprints size={16} /> Photos and videos you post</li>
                        <li className="flex items-center gap-2"><Footprints size={16} /> Comments and messages</li>
                        <li className="flex items-center gap-2"><Footprints size={16} /> Websites you visit</li>
                        <li className="flex items-center gap-2"><Footprints size={16} /> Things you search for</li>
                        <li className="flex items-center gap-2"><Footprints size={16} /> Apps you download</li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Active vs Passive Footprints",
        icon: "🔄",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg">There are two types of digital footprints you leave behind:</p>
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-indigo-50 p-6 rounded-xl border-2 border-indigo-200">
                        <h3 className="font-bold text-indigo-800 mb-3">✍️ Active Footprint</h3>
                        <p className="text-slate-600 mb-3">Things you <em>choose</em> to share:</p>
                        <ul className="text-indigo-700 text-sm space-y-2">
                            <li>• Posting on social media</li>
                            <li>• Filling out forms</li>
                            <li>• Sending emails</li>
                            <li>• Creating accounts</li>
                        </ul>
                    </div>
                    <div className="bg-purple-50 p-6 rounded-xl border-2 border-purple-200">
                        <h3 className="font-bold text-purple-800 mb-3">👀 Passive Footprint</h3>
                        <p className="text-slate-600 mb-3">Data collected <em>without</em> you knowing:</p>
                        <ul className="text-purple-700 text-sm space-y-2">
                            <li>• Websites tracking your visits</li>
                            <li>• Apps recording your location</li>
                            <li>• Cookies remembering you</li>
                            <li>• Your IP address being logged</li>
                        </ul>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Why It Matters",
        icon: "⚠️",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg mb-4">Your digital footprint can affect your future in real ways:</p>
                <div className="grid md:grid-cols-3 gap-5">
                    <div className="bg-amber-50 p-5 rounded-xl border-2 border-amber-200">
                        <div className="text-3xl mb-3">🏫</div>
                        <h3 className="font-bold text-amber-800 mb-2">Schools Check</h3>
                        <p className="text-slate-600 text-sm">
                            Colleges and schools may look at your social media when you apply.
                            Embarrassing posts could hurt your chances!
                        </p>
                    </div>
                    <div className="bg-amber-50 p-5 rounded-xl border-2 border-amber-200">
                        <div className="text-3xl mb-3">💼</div>
                        <h3 className="font-bold text-amber-800 mb-2">Future Jobs</h3>
                        <p className="text-slate-600 text-sm">
                            Over 70% of employers check social media before hiring.
                            What you post today could affect jobs later!
                        </p>
                    </div>
                    <div className="bg-amber-50 p-5 rounded-xl border-2 border-amber-200">
                        <div className="text-3xl mb-3">♾️</div>
                        <h3 className="font-bold text-amber-800 mb-2">Forever Online</h3>
                        <p className="text-slate-600 text-sm">
                            Even if you delete something, it might already be saved or screenshot by others.
                            The internet never forgets!
                        </p>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 4,
        title: "What to Keep Private",
        icon: "🔒",
        content: (
            <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-red-50 p-6 rounded-xl border-2 border-red-200">
                        <h3 className="font-bold text-red-700 mb-3">🚫 NEVER Share These:</h3>
                        <ul className="text-slate-700 text-sm space-y-2">
                            <li className="flex items-center gap-2"><X size={16} className="text-red-500" /> Your home address</li>
                            <li className="flex items-center gap-2"><X size={16} className="text-red-500" /> Phone number</li>
                            <li className="flex items-center gap-2"><X size={16} className="text-red-500" /> School name and location</li>
                            <li className="flex items-center gap-2"><X size={16} className="text-red-500" /> Passwords (to ANYONE!)</li>
                            <li className="flex items-center gap-2"><X size={16} className="text-red-500" /> When you're home alone</li>
                            <li className="flex items-center gap-2"><X size={16} className="text-red-500" /> Vacation plans (while away)</li>
                        </ul>
                    </div>
                    <div className="bg-amber-50 p-6 rounded-xl border-2 border-amber-200">
                        <h3 className="font-bold text-amber-700 mb-3">⚠️ Think Twice About:</h3>
                        <ul className="text-slate-700 text-sm space-y-2">
                            <li>🤔 Full birthday (birth year)</li>
                            <li>🤔 Photos showing your location</li>
                            <li>🤔 Pictures of your school</li>
                            <li>🤔 Embarrassing photos of friends</li>
                            <li>🤔 Strong opinions that could hurt later</li>
                            <li>🤔 Screenshots of private conversations</li>
                        </ul>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 5,
        title: "Pro Tips",
        icon: "💡",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg mb-4">Keep your digital footprint clean with these tips:</p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-5 rounded-xl border-2 border-green-200">
                        <h3 className="font-bold text-green-700 mb-2">🔍 Google Yourself</h3>
                        <p className="text-slate-600 text-sm">
                            Search your name online to see what others can find about you. You might be surprised!
                        </p>
                    </div>
                    <div className="bg-green-50 p-5 rounded-xl border-2 border-green-200">
                        <h3 className="font-bold text-green-700 mb-2">📸 The Grandma Test</h3>
                        <p className="text-slate-600 text-sm">
                            Before posting, ask: "Would I be okay if my grandma saw this?" If not, don't post it!
                        </p>
                    </div>
                    <div className="bg-green-50 p-5 rounded-xl border-2 border-green-200">
                        <h3 className="font-bold text-green-700 mb-2">⚙️ Check Privacy Settings</h3>
                        <p className="text-slate-600 text-sm">
                            Set your accounts to private so only approved friends can see your posts.
                        </p>
                    </div>
                    <div className="bg-green-50 p-5 rounded-xl border-2 border-green-200">
                        <h3 className="font-bold text-green-700 mb-2">🧹 Clean Up Regularly</h3>
                        <p className="text-slate-600 text-sm">
                            Every few months, look through old posts and delete anything you wouldn't want future-you to see.
                        </p>
                    </div>
                </div>
                <div className="bg-indigo-100 p-4 rounded-xl border border-indigo-200 mt-4">
                    <h4 className="font-bold text-indigo-800 mb-2">🎮 Ready to Practice?</h4>
                    <p className="text-indigo-700 text-sm">
                        In the activity, you'll decide what should be public vs private!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ FOOTPRINT DATA ============
interface FootprintItem {
    id: number;
    type: 'post' | 'photo' | 'search' | 'like' | 'share';
    content: string;
    isPublic: boolean;
    icon: string;
}

const INITIAL_FOOTPRINTS: FootprintItem[] = [
    { id: 1, type: 'post', content: 'I love pizza! 🍕', isPublic: true, icon: '📝' },
    { id: 2, type: 'photo', content: 'My school photo', isPublic: true, icon: '📸' },
    { id: 3, type: 'search', content: 'How to beat level 5', isPublic: false, icon: '🔍' },
    { id: 4, type: 'like', content: 'Liked: Cute cat video', isPublic: true, icon: '❤️' },
    { id: 5, type: 'share', content: 'Shared my address', isPublic: true, icon: '📍' },
    { id: 6, type: 'post', content: 'My phone number is...', isPublic: true, icon: '📱' },
];

// ============ GAME COMPONENT ============
const FootprintGame = ({ onComplete }: { onComplete: () => void }) => {
    const [footprints, setFootprints] = useState(INITIAL_FOOTPRINTS);
    const [score, setScore] = useState(0);
    const [showResults, setShowResults] = useState(false);
    const [completed, setCompleted] = useState(false);

    const toggleVisibility = (id: number) => {
        setFootprints(prev => prev.map(f =>
            f.id === id ? { ...f, isPublic: !f.isPublic } : f
        ));
    };

    const checkFootprint = () => {
        const privateCount = footprints.filter(f => !f.isPublic).length;
        const dangerousPublic = footprints.filter(f =>
            f.isPublic && (f.content.includes('address') || f.content.includes('phone'))
        ).length;

        const newScore = Math.max(0, privateCount * 17 - dangerousPublic * 20);
        setScore(Math.min(100, newScore));
        setShowResults(true);

        if (newScore >= 50 && !completed) {
            setCompleted(true);
            onComplete();
        }
    };

    const reset = () => {
        setFootprints(INITIAL_FOOTPRINTS);
        setShowResults(false);
        setScore(0);
    };



    return (
        <div className="space-y-6">
            <div className="text-center">
                <h2 className="text-3xl font-black text-slate-800 mb-2 flex items-center justify-center gap-3">
                    <Footprints className="text-indigo-600" /> Your Online Trail
                </h2>
                <p className="text-slate-600">
                    Decide what should be <span className="text-green-600 font-bold">private</span> vs <span className="text-red-600 font-bold">public</span>!
                </p>
            </div>

            {/* Footprint Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {footprints.map(item => (
                    <motion.div
                        key={item.id}
                        layout
                        className={`p-4 rounded-2xl border-2 transition-all ${item.isPublic
                            ? 'bg-red-50 border-red-200'
                            : 'bg-green-50 border-green-200'
                            }`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <span className="text-2xl">{item.icon}</span>
                                <div>
                                    <div className="font-bold text-slate-800">{item.content}</div>
                                    <div className={`text-xs font-bold ${item.isPublic ? 'text-red-600' : 'text-green-600'}`}>
                                        {item.isPublic ? '🌍 Everyone can see' : '🔒 Only you'}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => toggleVisibility(item.id)}
                                className={`p-3 rounded-xl ${item.isPublic
                                    ? 'bg-red-500 hover:bg-red-600'
                                    : 'bg-green-500 hover:bg-green-600'
                                    } text-white`}
                            >
                                {item.isPublic ? <Eye size={20} /> : <EyeOff size={20} />}
                            </button>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Check/Results */}
            {!showResults ? (
                <div className="text-center">
                    <button
                        onClick={checkFootprint}
                        className="bg-indigo-500 text-white px-8 py-4 rounded-2xl font-bold text-xl hover:bg-indigo-600 shadow-lg"
                    >
                        Check My Footprint! 🔍
                    </button>
                    <div className="mt-4 text-slate-500 text-sm">
                        💡 Tip: Make private stuff that strangers shouldn't see!
                    </div>
                </div>
            ) : (
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-white rounded-3xl p-8 shadow-xl text-center"
                >
                    <div className="text-6xl mb-4">⚠️</div>
                    <h2 className="text-3xl font-black text-slate-800 mb-2">
                        Privacy Score: {score}%
                    </h2>
                    <p className="text-slate-600 mb-6">
                        Be careful! Your address and phone should be private.
                    </p>
                    <button onClick={reset} className="bg-indigo-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-indigo-600 flex items-center gap-2 mx-auto">
                        <RotateCcw size={18} /> Try Again
                    </button>
                </motion.div>
            )}
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const DigitalFootprint = () => {
    // Optimized selector to prevent re-renders from unrelated store changes
    const completeModule = useAcademyStore(state => state.completeModule);
    const [completed, setCompleted] = useState(false);

    const handleComplete = React.useCallback(() => {
        setCompleted(true);
        completeModule('digital-footprint', 100);
    }, [completeModule]);

    return (
        <LessonWrapper
            moduleName="Digital Footprint"
            moduleEmoji="👣"
            moduleColor="purple"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Activity"
        >
            <FootprintGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default DigitalFootprint;
